// One note, open. Text, a title, autosave and a word count - and, since
// 2026-08-19, a small markdown vocabulary with a Write/Read toggle over it.
//
// WHAT THE FORMATTING SLICE IS AND IS NOT. The pinned area and note sections had
// already superseded part of `prototype-v1.md` §6; this changes its still-current
// no-formatting clause WITHOUT a toolbar and leaves those groups intact.
// Formatting is typed - `#`, `-`, `**` - the way Obsidian does it on a desktop,
// the shortcuts below put that syntax in for you, and the only resting control
// is the two-word Write/Read segment in the footer, which switches a VIEW rather
// than doing anything to the note. It adds no folders or tags, and there is no
// font, colour, size or alignment control anywhere - the line this product holds
// is that formatting is semantic and expressible in plain text, because anything
// else puts an asterisk on the "your notes export as markdown" promise the
// product has already made.
//
//   READING IS OPT-IN, PER VIEW, and that is a data-safety decision rather than
//   a preference. A note written last week containing `*emphasis*` or a line
//   starting `- ` READS DIFFERENTLY the day a renderer ships, and nobody asked
//   for that. So the toggle starts in Write every time this editor opens,
//   nothing is stored, and anyone who never presses Read sees exactly what they
//   saw before. The bytes were never at risk - `note.body` is `text` and no
//   migration ran - but how somebody's own words read is worth asking about.
//
//   SMART EDITS USE ONE `execCommand` - normally `insertText`, with `delete` for
//   the unavoidable empty removal - WHICH IS THE WHOLE OF WHY `applyEdit` LOOKS
//   LIKE THAT. Assigning `.value` (what an ordinary React state update does)
//   destroys the browser's native undo stack, measured in Chrome.
//   `note-typing.ts` carries the measurement and the reasoning.
//
//
// AUTOSAVE IS THE RISKY PART OF THIS SCREEN, so the rules it runs on are here
// rather than left to be inferred:
//
//   NOTHING LEAVES WITHOUT ITS TEXT. Back saves first and only closes if the
//   save landed; a failure keeps you here, beside the words, with a Try again.
//   Leaving any OTHER way - a tab, the browser's own Back - unmounts this, so
//   the same save is fired from the teardown and its failure is said on the
//   toast. Losing what somebody typed is the one outcome this screen may not
//   have.
//
//   THE CHIP NEVER SAYS SAVED WHILE IT IS NOT. It says `Saving…` from the
//   keystroke that made the note dirty, not from the request going out, because
//   the eight hundred milliseconds in between are exactly when somebody glances
//   at it and decides it is safe to close the laptop.
//
//   A FAILURE A RETRY CANNOT FIX DOES NOT OFFER ONE. Almost everything that goes
//   wrong here is worth another go - a dropped connection, a server that was
//   not there - and gets one. A REFUSED request is the exception: a title past
//   the length the API takes is refused the same way however many times it is
//   sent, so what the footer draws is the server's own sentence, which names the
//   field and the number, with no Try again beside it. A dead end dressed as a
//   retry is worse than an honest refusal, because the person keeps pressing.
//
//   A NEW NOTE IS NOT WRITTEN UNTIL THERE IS SOMETHING IN IT. The editor opens
//   on a draft rather than on a row, so opening New note and changing your mind
//   leaves nothing behind to find and delete.
//
//   AN ITEM'S NOTE HAS NO TITLE TO EDIT. `title` there is the item's own, and
//   renaming somebody's gym session because they tidied a heading on this screen
//   is not something the Notes screen may do. It is drawn, not typed in, and
//   only the body is sent.
//
//   DELETING DOES NOT ASK. Both kinds are recoverable - a note of its own is
//   deleted into the list's own Deleted group, and taking the note off an item
//   puts the text back on Undo - so a confirm here would be a question about
//   something that can be answered afterwards. The one act that cannot be
//   undone is Delete for good, and that one asks, in the list where it lives.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  api,
  isRefused,
  isSignedOut,
  reasonFor,
  requireAppliedRoutineNoteWrite,
} from '../api/client.ts';
import type { Note, UpdatedRoutine } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { hueIn } from '../edit/edit-model.ts';
import { useMode } from '../shell/theme.ts';
import { NoteMarkdown } from './NoteMarkdown.tsx';
import { usesPrimaryModifier } from './note-formatting.ts';
import { fenceOwnsRange } from './note-markdown.ts';
import { ShareNote } from './ShareNote.tsx';
import { VisualPageNavigator } from './VisualPageNavigator.tsx';
import { draftOf, sameDraft, whereLabel, wordsLabel, worthSaving } from './note-model.ts';
import { useVisualPages } from './useVisualPages.ts';
import { visualPageDividers } from './visual-pages.ts';
import type { Draft, Saved } from './note-model.ts';
import type { RoutineNoteClear } from './useNotes.ts';
import {
  continueList,
  indentLines,
  isUrl,
  linkSelection,
  toggleTask,
  wrapSelection,
} from './note-typing.ts';
import type { Edit } from './note-typing.ts';

/**
 * How long typing stops before what is on screen is sent.
 *
 * The prototype's own 700ms, rounded up a little: it has to be longer than the
 * gap between two keystrokes in a sentence and shorter than the gap between
 * finishing one and reaching for the Back button.
 */
export const AUTOSAVE_MS = 800;

interface Props {
  /** The row being edited, or null for a note nobody has written yet. */
  note: Note | null;
  /**
   * Leave. `saved` names what the last write left on the server, or null when
   * this editor wrote nothing at all - an editor opened and closed again says
   * nothing, because nothing happened.
   *
   * It carries the row this editor OPENED with as well, because a save can take
   * the note off the screen it is going back to. That clear is reported at the
   * successful write below; `close` uses the opening kind to avoid replacing it
   * with a generic saved sentence.
   */
  onClose: (saved: Saved | null) => void;
  /**
   * Answers whether the note was actually removed. A delete that failed leaves
   * this editor on screen with the words still in it, and it has to know that:
   * see the button, where pending writes are settled before the request.
   */
  onDelete: (note: Note, clearRoutineNote?: () => Promise<RoutineNoteClear>) => Promise<boolean>;
  onSignedOut: () => void;
  /** The transient line at the foot of the screen, for a failure on the way out. */
  say: (message: string) => void;
  /**
   * A read-only link was made or turned off, so the list behind this screen is
   * out of date.
   *
   * Separate from `onClose`'s `saved`, which reports what was WRITTEN INTO the
   * note: sharing changes not one character of it, so it would never be reported
   * there - and the list draws a badge on every published note, which is a
   * safety property rather than decoration. A write that changes what the list
   * says has to tell the list (AGENTS.md, the board's `refresh()`).
   */
  onShareChanged: (noteId: string, shared: boolean) => void;
  onRoutineNoteChanged: () => void;
  onRoutineNoteSaved: (noteId: string) => void;
  onRoutineNoteSaveFailed: (noteId: string, message: string) => void;
  onRoutineNoteCleared: (
    note: Note,
    restoreBody: (() => Promise<void>) | null,
    retireEditorClear: () => void,
  ) => void;
  shareStatus: boolean | undefined;
}

type Status = 'idle' | 'saving' | 'saved' | 'failed';

/**
 * What the chip says once nothing is pending.
 *
 * THE CHIP'S JOB IS TO ANSWER ONE QUESTION: is what I am looking at safely
 * stored. So at rest it keys off whether the note EXISTS on the server, not off
 * whether this editor session happened to write to it - an existing note opened
 * and left alone, or typed in and put back the way it was, is stored, and the
 * honest answer is yes. Blank is kept for the one case where there is genuinely
 * nothing stored: a brand-new note nothing has been written for yet.
 *
 * Blank is not neutral, which is the reasoning behind that. A label that was
 * there a moment ago and then vanishes reads as something having gone wrong, so
 * the person re-types or saves again to be sure - and withholding a true answer
 * exactly when somebody is looking for reassurance is its own kind of failure.
 *
 * The invariant in the other direction is untouched: this is only ever reached
 * with nothing left to send, so the chip still never reads Saved while it is
 * not, and `Saving…` still goes up from the keystroke rather than from the
 * request.
 */
function settled(stored: boolean): Status {
  return stored ? 'saved' : 'idle';
}

/**
 * The two views, and the segment in the footer is the whole of the control.
 *
 * WRITE IS ALWAYS WHERE THIS OPENS. See the header: rendering re-reads words
 * somebody already wrote, so it is asked for rather than assumed, and nothing
 * about the choice is stored - not in `localStorage`, not on the account.
 * Making it stick is one call to `shell/preference.ts` on the day somebody
 * decides it should, and that day is a decision rather than a refactor.
 */
type View = 'write' | 'read';

const VIEWS: readonly { value: View; label: string }[] = [
  { value: 'write', label: 'Write' },
  { value: 'read', label: 'Read' },
];

/**
 * The placeholder, which is R3 - "discovery without chrome" - and the only place
 * the vocabulary is named on the daily path.
 *
 * Markdown shorthand is depth with no surface: a stranger will not type `##`, so
 * a renderer and a set of shortcuts alone would leave this screen feeling
 * exactly as it did. Two shorthands, on screen only while the note is EMPTY, so
 * it is a hint rather than furniture. The Formatting card in Settings is the
 * rest of it.
 *
 * PLAIN IN BOTH VOICES, deliberately, and it is not in `voice.ts` at all. The
 * whole sentence after the first clause is a FACT - `#` makes a heading, in
 * every voice - and a fact carries identically in both columns (`voice.ts`,
 * boundary 3). There is nothing here for a register to move.
 */
const HINT = '# makes a heading, - makes a list.';
const PLACEHOLDER = {
  note: `Start typing. ${HINT}`,
  item: `Anything you want to remember about this one. ${HINT}`,
};

/**
 * The native `value` setter, kept because React's own tracker hides an
 * assignment made through the property. Only the fallback below uses it.
 */
const setNativeValue = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;

/**
 * Make an edit to the textarea, in the one way that keeps `Cmd+Z` working.
 *
 * `document.execCommand('insertText')` is deprecated and has no replacement
 * that preserves the native editing history this needs. Measured in Chrome on
 * a controlled React textarea exactly like the one below: it updates the DOM,
 * keeps React's state in step, fires `onChange` ONCE so the existing autosave
 * runs unchanged, and leaves `Cmd+Z` working - and the undo itself fires
 * `onChange` again, so the autosave saves the undo too. Assigning `.value`
 * instead - which is what a plain `setState` does - left `Cmd+Z` doing nothing.
 *
 * THE FALLBACK IS SECOND. It exists so that an environment where `execCommand`
 * is unavailable or refuses the edit - jsdom, or a browser that removes it one
 * day - still applies the edit rather than dropping the keystroke, and it goes
 * through the same `input` event so there is one state path and not two. It
 * cannot keep the undo stack; nothing else can.
 */
function applyEdit(field: HTMLTextAreaElement, edit: Edit): void {
  field.focus();
  field.setSelectionRange(edit.start, edit.end);

  if (typeof document.execCommand === 'function') {
    let done: boolean;
    try {
      // An empty insert is the one shape `insertText` is not reliable for, and
      // `continueList` reaches it only when the list starts on the note's very
      // first line. `delete` is the undoable deletion for that one case.
      done =
        edit.text === ''
          ? document.execCommand('delete')
          : document.execCommand('insertText', false, edit.text);
    } catch {
      done = false;
    }
    if (done) {
      field.setSelectionRange(edit.select[0], edit.select[1]);
      return;
    }
  }

  const next = field.value.slice(0, edit.start) + edit.text + field.value.slice(edit.end);
  setNativeValue?.call(field, next);
  field.setSelectionRange(edit.select[0], edit.select[1]);
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.setSelectionRange(edit.select[0], edit.select[1]);
}

/**
 * What a key press means in the note's body, or null for "not ours".
 *
 * NULL IS AS IMPORTANT AS THE EDITS. Every key this does not claim reaches the
 * textarea's own behaviour, which is what keeps Enter in prose an ordinary
 * Enter and Tab outside a list a way to leave the field.
 *
 * A KEY THAT IS COMMITTING AN IME COMPOSITION IS NOT OURS, and that is the same
 * mechanism: while a candidate window is open the Enter that accepts it is the
 * input method's, and claiming it puts `\n- ` into the middle of text the person
 * has not finished typing. Somebody writing a note in Japanese, Chinese or
 * Korean presses it on every phrase, so this is the whole of the list on those
 * keyboards rather than an edge case.
 *
 * FENCED CODE OWNS ITS RANGE TOO. The parser's fence boundary is reused here,
 * so list-shaped snippets and modifier keys inside them remain literal text.
 *
 * BOLD AND LINK USE THE PLATFORM'S APPLICATION MODIFIER: Command on macOS,
 * Control elsewhere. Italic and task toggling are the two settled exceptions
 * that accept either modifier.
 */
function smartEdit(value: string, from: number, to: number, make: () => Edit | null): Edit | null {
  return fenceOwnsRange(value, from, to) ? null : make();
}

function editFor(event: ReactKeyboardEvent<HTMLTextAreaElement>): Edit | null {
  if (event.nativeEvent.isComposing) return null;

  const field = event.currentTarget;
  const value = field.value;
  const from = field.selectionStart ?? 0;
  const to = field.selectionEnd ?? from;

  return smartEdit(value, from, to, () => {
    const mod = event.metaKey || event.ctrlKey;

    // Before plain Enter, or a list would be continued instead of ticked.
    if (event.key === 'Enter' && mod && !event.altKey) return toggleTask(value, from);
    if (event.key === 'Enter' && !mod && !event.shiftKey && !event.altKey && from === to) {
      return continueList(value, from);
    }
    if (event.key === 'Tab' && !mod && !event.altKey) {
      return indentLines(value, from, to, event.shiftKey);
    }
    if (event.shiftKey || event.altKey) return null;
    switch (event.key.toLowerCase()) {
      case 'b':
        return usesPrimaryModifier(event) ? wrapSelection(value, from, to, '**') : null;
      case 'i':
        return mod ? wrapSelection(value, from, to, '*') : null;
      case 'k':
        return usesPrimaryModifier(event) ? linkSelection(value, from, to) : null;
      default:
        return null;
    }
  });
}

/**
 * Size a field to what is in it.
 *
 * A note is prose and a fixed-height box hides the end of a long one, which is
 * a defect the prototype's own screenshot review caught. It is done here rather
 * than with `field-sizing: content` because that property is too new to rely on
 * for a rule whose failure mode is hiding what somebody typed - a browser
 * without it would draw a heading with its second line cut off and say nothing.
 *
 * Called as a ref for the first paint and from `onChange` for every one after:
 * a ref callback runs when the element arrives, not when its value moves.
 */
function grow(field: HTMLTextAreaElement | null): void {
  if (field === null) return;
  field.style.height = 'auto';
  // jsdom has no layout and answers 0 here, which would collapse the field to
  // nothing; a real browser always reports at least one line.
  if (field.scrollHeight > 0) field.style.height = `${field.scrollHeight}px`;
}

export function NoteEditor({
  note,
  onClose,
  onDelete,
  onSignedOut,
  say,
  onShareChanged,
  onRoutineNoteChanged,
  onRoutineNoteSaved,
  onRoutineNoteSaveFailed,
  onRoutineNoteCleared,
  shareStatus,
}: Props) {
  const mode = useMode();
  const [row, setRow] = useState<Note | null>(note);
  const [draft, setDraft] = useState<Draft>(() => draftOf(note));
  const [status, setStatus] = useState<Status>(note === null ? 'idle' : 'saved');
  const [failure, setFailure] = useState<string | null>(null);
  // Whether sending the same words again could ever end differently. Only ever
  // read while `status` is 'failed', and written beside every failure there is.
  const [retryable, setRetryable] = useState(true);
  const [leaving, setLeaving] = useState(false);
  // Opt-in, and per view. See `View` above for why it is not remembered.
  const [view, setView] = useState<View>('write');
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const writeFocusRef = useRef<HTMLTextAreaElement | null>(null);
  const prepareVisualPageContent = useCallback(() => {
    grow(titleRef.current);
    grow(bodyRef.current);
  }, []);
  const visualPages = useVisualPages(prepareVisualPageContent);

  // The save loop runs outside React's render, and has to see what is on screen
  // NOW rather than what was on screen when it started: somebody typing through
  // a slow save is the ordinary case, not the odd one.
  const draftRef = useRef<Draft>(draft);
  const savedRef = useRef<Draft>(draftOf(note));
  const rowRef = useRef<Note | null>(note);
  // The save that is running, or null when none is. It is the PROMISE rather
  // than a flag because a caller that arrives mid-save has to be able to wait
  // for it - see `flush`.
  const inFlightRef = useRef<Promise<string | null> | null>(null);
  // How the save that is running is STOPPED, rather than waited for. One
  // controller per run, dropped with it. See the delete button.
  const abortRef = useRef<AbortController | null>(null);
  const wroteRef = useRef(false);
  // Raised the moment a note row is deleted, so neither the loop below nor the
  // teardown puts what was deleted straight back. See the reason at each.
  const discardedRef = useRef(false);
  const routinePausedRef = useRef(false);
  const pendingClearRef = useRef<{
    noteId: string;
    body: string;
    operationId: string;
  } | null>(null);
  const mountedRef = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showDraft = useCallback((next: Draft) => {
    draftRef.current = next;
    if (mountedRef.current) setDraft(next);
  }, []);

  const showRoutineBody = useCallback(
    (current: Note, body: string, routineRevision: string, replaceDraft = true) => {
      const stored = { ...savedRef.current, body };
      savedRef.current = stored;
      if (replaceDraft) showDraft(stored);
      const active = rowRef.current;
      const next =
        active?.kind === 'item' && active.id === current.id
          ? { ...active, body, routineRevision }
          : { ...current, body, routineRevision };
      rowRef.current = next;
      if (mountedRef.current) setRow(next);
    },
    [showDraft],
  );

  const pauseRoutineQueue = useCallback(async () => {
    routinePausedRef.current = true;
    if (timer.current !== null) clearTimeout(timer.current);
    if (mountedRef.current) setLeaving(true);
    const running = inFlightRef.current;
    if (running !== null) await running;
  }, []);

  const updateRoutineThroughQueue = useCallback(
    async (current: Note, body: string, keepDraftOnFailure: boolean): Promise<UpdatedRoutine> => {
      const draftBefore = draftRef.current;
      await pauseRoutineQueue();
      if (!keepDraftOnFailure) showDraft(savedRef.current);

      let updated: UpdatedRoutine;
      try {
        const active = rowRef.current;
        updated = await api.updateRoutine(current.id, {
          note: body,
          ...(body === '' && active?.kind === 'item' && active.id === current.id
            ? {
                noteClearExpectedNote: active.body,
                noteClearExpectedRevision: active.routineRevision ?? undefined,
              }
            : {}),
        });
      } catch (error) {
        showDraft(keepDraftOnFailure ? draftBefore : savedRef.current);
        routinePausedRef.current = false;
        if (mountedRef.current) setLeaving(false);
        throw error;
      }

      showRoutineBody(current, updated.note, updated.revision);
      try {
        requireAppliedRoutineNoteWrite(updated);
      } catch (error) {
        routinePausedRef.current = false;
        if (mountedRef.current) setLeaving(false);
        throw error;
      }
      wroteRef.current = true;
      if (mountedRef.current) {
        setFailure(null);
        setStatus('saved');
      }
      return updated;
    },
    [pauseRoutineQueue, showDraft, showRoutineBody],
  );

  const restoreRoutineBody = useCallback(
    async (current: Note, body: string, operationId: string) => {
      await pauseRoutineQueue();
      showDraft(savedRef.current);
      try {
        const updated = await api.updateRoutine(current.id, {
          note: body,
          noteRestoreOperationId: operationId,
        });
        showRoutineBody(current, updated.note, updated.revision);
        requireAppliedRoutineNoteWrite(updated);
        wroteRef.current = true;
        pendingClearRef.current = null;
        if (mountedRef.current) {
          setFailure(null);
          setStatus('saved');
        }
      } catch (error) {
        routinePausedRef.current = false;
        if (mountedRef.current) setLeaving(false);
        throw error;
      }
    },
    [pauseRoutineQueue, showDraft, showRoutineBody],
  );

  const clearRoutineNote = useCallback(
    async (current: Note): Promise<RoutineNoteClear> => {
      await pauseRoutineQueue();
      if (savedRef.current.body === '') {
        showDraft(savedRef.current);
        routinePausedRef.current = false;
        if (mountedRef.current) setLeaving(false);
        return {
          changed: false,
          previousBody: null,
          restore: () => Promise.resolve(),
          retire: () => undefined,
        };
      }

      const updated = await updateRoutineThroughQueue(current, '', true);
      const previousBody = updated.previousNote;
      const operationId = updated.noteClearOperationId;
      const clear =
        previousBody === null || previousBody === '' || operationId === null
          ? null
          : { noteId: current.id, body: previousBody, operationId };
      let completion: 'recoverable' | 'unavailable' | 'superseded' = 'unavailable';
      try {
        if (clear !== null) completion = await api.completeRoutineNoteClear(updated);
      } catch (error) {
        if (isSignedOut(error)) onSignedOut();
      }
      if (completion === 'superseded') {
        pendingClearRef.current = null;
        routinePausedRef.current = false;
        if (mountedRef.current) setLeaving(false);
        return {
          changed: false,
          previousBody: null,
          restore: () => Promise.resolve(),
          retire: () => undefined,
        };
      }
      const recoverableClear = completion === 'recoverable' ? clear : null;
      pendingClearRef.current = recoverableClear;
      routinePausedRef.current = false;
      if (mountedRef.current) setLeaving(false);
      return {
        changed: true,
        previousBody: recoverableClear?.body ?? null,
        restore: () =>
          recoverableClear === null
            ? Promise.resolve()
            : restoreRoutineBody(current, recoverableClear.body, recoverableClear.operationId),
        retire: () => {
          if (pendingClearRef.current === recoverableClear) pendingClearRef.current = null;
          if (recoverableClear !== null) {
            void api
              .retireRoutineNoteClear(recoverableClear.noteId, recoverableClear.operationId)
              .catch(() => undefined);
          }
        },
      };
    },
    [onSignedOut, pauseRoutineQueue, restoreRoutineBody, showDraft, updateRoutineThroughQueue],
  );

  /**
   * The loop itself. Started by `flush` below, which is the only caller: it is
   * `flush` that decides whether one is wanted, and there is never more than
   * one of these running.
   *
   * The loop rather than one request is what makes typing through a save
   * correct: the draft can move while the request is out, and the pass that
   * follows picks up whatever it moved to.
   *
   * A DISCARD STOPS IT WHEREVER IT IS. `discardedRef` is read before every
   * request and again after every answer, and on a discard the loop stops
   * without sending anything else and without recording anything as saved -
   * because what it was carrying is text somebody has just deleted.
   */
  const send = useCallback(
    async (signal: AbortSignal): Promise<string | null> => {
      setStatus('saving');
      try {
        while (!sameDraft(draftRef.current, savedRef.current)) {
          if (discardedRef.current || routinePausedRef.current) return null;
          const attempt = draftRef.current;
          let stored = attempt;
          const current = rowRef.current;

          if (current === null) {
            // Nothing worth writing yet. Left unsaved deliberately: `savedRef`
            // stays behind the draft, so the next keystroke tries again.
            if (!worthSaving(attempt)) break;
            const made = await api.createNote(attempt, signal);
            if (discardedRef.current) return null;
            rowRef.current = made;
            setRow(made);
          } else if (current.kind === 'item') {
            // The one column this screen may write on an item, through the route
            // that has always written it. The title is the item's and is not sent.
            const updated = await api.updateRoutine(current.id, {
              note: attempt.body,
              ...(attempt.body.trim() === ''
                ? {
                    noteClearExpectedNote: current.body,
                    noteClearExpectedRevision: current.routineRevision ?? undefined,
                  }
                : {}),
            });
            if (discardedRef.current) return null;
            if (updated.noteWriteApplied === false) {
              showRoutineBody(
                current,
                updated.note,
                updated.revision,
                sameDraft(draftRef.current, attempt),
              );
              requireAppliedRoutineNoteWrite(updated);
            }
            stored = { ...attempt, body: updated.note };
            if (sameDraft(draftRef.current, attempt) && !sameDraft(attempt, stored)) {
              draftRef.current = stored;
              setDraft(stored);
            }
            onRoutineNoteSaved(current.id);
            onRoutineNoteChanged();
            if (
              typeof updated.previousNote === 'string' &&
              updated.previousNote !== '' &&
              updated.note === '' &&
              updated.noteClearOperationId !== null
            ) {
              const clear = {
                noteId: current.id,
                body: updated.previousNote,
                operationId: updated.noteClearOperationId,
              };
              let completion: 'recoverable' | 'unavailable' | 'superseded' = 'unavailable';
              try {
                completion = await api.completeRoutineNoteClear(updated);
              } catch (error) {
                if (isSignedOut(error)) onSignedOut();
              }
              if (completion !== 'superseded') {
                const recoverable = completion === 'recoverable';
                pendingClearRef.current = recoverable ? clear : null;
                onRoutineNoteCleared(
                  current,
                  recoverable
                    ? () => restoreRoutineBody(current, clear.body, clear.operationId)
                    : null,
                  () => {
                    if (pendingClearRef.current === clear) pendingClearRef.current = null;
                    if (recoverable) {
                      void api
                        .retireRoutineNoteClear(clear.noteId, clear.operationId)
                        .catch(() => undefined);
                    }
                  },
                );
              } else {
                pendingClearRef.current = null;
              }
            } else {
              pendingClearRef.current = null;
            }
            const next: Note = {
              ...current,
              body: updated.note,
              routineRevision: updated.revision,
            };
            rowRef.current = next;
            setRow(next);
          } else {
            // The server's answer replaces the row, `heading` included - so an
            // untitled note picking up a heading from its first line is the
            // server's decision arriving, not one made here.
            const next = await api.updateNote(current.id, attempt, signal);
            if (discardedRef.current) return null;
            rowRef.current = next;
            setRow(next);
          }

          savedRef.current = stored;
          wroteRef.current = true;
        }

        if (discardedRef.current || routinePausedRef.current) return null;
        setFailure(null);
        setStatus(settled(rowRef.current !== null));
        return null;
      } catch (error) {
        // A CANCELLED SAVE IS NOT A FAILED ONE. The abort below is this editor
        // doing what the person asked when they pressed delete, so it may not
        // reach the footer or the toast: nothing went wrong, and "Not saved"
        // about a note they have just deleted is a sentence with no answer to it.
        if (discardedRef.current) return null;
        if (isSignedOut(error)) {
          // The whole shell is about to be replaced by the sign-in gate, so
          // there is nothing to write into this footer and nothing to say on a
          // toast that goes with it.
          onSignedOut();
          return null;
        }
        // The server's own sentence about the field it would not take, where
        // there is one - "That is not a valid request body" is true and cannot
        // be acted on, and this is a footer somebody has to act on.
        const reason = reasonFor(error);
        setFailure(reason);
        setRetryable(!isRefused(error));
        setStatus('failed');
        return reason;
      }
    },
    [
      onRoutineNoteChanged,
      onRoutineNoteCleared,
      onRoutineNoteSaved,
      onSignedOut,
      restoreRoutineBody,
      showRoutineBody,
    ],
  );

  /**
   * Send what is on screen, and answer once the server has it.
   *
   * The contract, which both the Back button and the teardown lean on: null
   * means THE SERVER HAS WHAT IS ON SCREEN, and a string is the reason it does
   * not. Nothing else may be returned - a caller told null closes the editor,
   * names the note on the toast and has the list re-read behind it.
   */
  const flush = useCallback((): Promise<string | null> => {
    // A SAVE ALREADY RUNNING IS THE ANSWER, and is awaited rather than reported
    // as nothing to do. Its loop re-reads `draftRef` after every await, so it
    // carries whatever has been typed since - but it has not LANDED yet, and
    // answering null here would close the editor over a request still in the
    // air: the note written and the list never re-read, or a failure arriving
    // after somebody was told it saved.
    const running = inFlightRef.current;
    if (running !== null) return running;
    if (routinePausedRef.current) return Promise.resolve(null);

    if (sameDraft(draftRef.current, savedRef.current)) {
      // Nothing to send - so the chip goes back to what is true, rather than
      // keeping up the `Saving…` the keystroke put there. Typing a letter and
      // taking it out again lands exactly here, and the chip may no more say
      // Saving for ever than it may say Saved while it is not.
      setStatus(settled(rowRef.current !== null));
      return Promise.resolve(null);
    }

    // One controller per run, so aborting this save can never reach into the
    // next one - the delete that fails leaves the editor open and saving again.
    const stop = new AbortController();
    abortRef.current = stop;
    const run = send(stop.signal);
    inFlightRef.current = run;
    // Lowered from OUT HERE rather than in a `finally` inside `send`: a pass
    // that never reaches an await - a new note with nothing worth writing in it
    // yet - has already finished by the time the line above runs, so a `finally`
    // of its own would clear the ref before it was ever set and leave a settled
    // promise standing in for a save for the rest of the editor's life.
    const done = () => {
      inFlightRef.current = null;
      if (abortRef.current === stop) abortRef.current = null;
    };
    void run.then(done, done);
    return run;
  }, [send]);

  const saveBeforeSharing = useCallback(async (): Promise<boolean> => {
    const reason = await flush();
    return (
      reason === null && !discardedRef.current && sameDraft(draftRef.current, savedRef.current)
    );
  }, [flush]);

  // Read through refs by the teardown below, which runs after the last render
  // and cannot depend on anything.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const sayRef = useRef(say);
  sayRef.current = say;
  const routineSaveFailedRef = useRef(onRoutineNoteSaveFailed);
  routineSaveFailedRef.current = onRoutineNoteSaveFailed;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
      // DELETING UNMOUNTS THIS TOO, and a pending save fired from here would
      // write the very text that was just deleted straight back - putting the
      // note on the item again, or filling a deleted note back in behind the
      // Undo. Nothing to save is exactly what "I deleted it" means.
      if (discardedRef.current || routinePausedRef.current) return;
      if (sameDraft(draftRef.current, savedRef.current)) return;
      // Left by a tab, by the browser's own Back, or by the session ending -
      // every way out of this screen that is not its own Back button. The save
      // still goes, and if it fails the toast is the only surface left to say
      // so on, because this footer is already gone.
      void flushRef.current().then((reason) => {
        if (reason === null) return;
        const message = `That note was not saved. ${reason}`;
        const current = rowRef.current;
        if (current?.kind === 'item') routineSaveFailedRef.current(current.id, message);
        else sayRef.current(message);
      });
    };
  }, []);

  /**
   * ⌘E - or Ctrl+E off a Mac - flips the view. Obsidian's own key for this.
   *
   * ON THE DOCUMENT rather than on the textarea, because in Read view there is
   * no field to have focus - a shortcut that only works in one of the two states
   * it switches between is a one-way door. This editor is mounted only while a
   * note is open, so the listener is scoped by its own lifetime.
   *
   * It uses the same platform modifier Settings prints. On macOS Ctrl+E remains
   * the native text-field binding for moving to the end of the line; elsewhere
   * Meta+E is not advertised or claimed. A composing input method keeps the key
   * until its candidate has committed, even though this listener is on document.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.isComposing || event.altKey || event.shiftKey) return;
      if (event.key.toLowerCase() !== 'e') return;
      if (!usesPrimaryModifier(event)) return;
      const field = bodyRef.current;
      if (
        view === 'write' &&
        event.target === field &&
        field !== null &&
        fenceOwnsRange(field.value, field.selectionStart ?? 0, field.selectionEnd ?? 0)
      ) {
        return;
      }
      event.preventDefault();
      setView((current) => (current === 'write' ? 'read' : 'write'));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view]);

  // Coming BACK from Read puts focus and the caret in the field where they were,
  // so Cmd+E twice leaves you typing rather than looking at a note with focus
  // nowhere. Only on a change of view: an existing note deliberately does not
  // steal focus when it opens.
  const shown = useRef(view);
  useEffect(() => {
    const moved = shown.current !== view;
    shown.current = view;
    if (moved && view === 'write') (writeFocusRef.current ?? bodyRef.current)?.focus();
  }, [view]);

  // ResizeObserver owns viewport, zoom and font reflow. This post-paint pass is
  // the older-browser floor for React value/view changes. Keeping it out of the
  // input event's layout path preserves fluid typing; it reads only, and never
  // rewrites text or selection.
  useEffect(() => {
    visualPages.measure();
  }, [draft.title, draft.body, view, visualPages.measure]);

  const change = (next: Draft) => {
    if (routinePausedRef.current) return;
    setDraft(next);
    // Set now rather than on the next render: the timer below fires outside
    // React and reads this, and a save that sent the previous keystroke's text
    // would leave the last letter behind on every pause.
    draftRef.current = next;
    // From the keystroke, not from the request. See the header.
    setStatus('saving');
    setFailure(null);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), AUTOSAVE_MS);
  };

  const leave = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    setLeaving(true);
    void (async () => {
      const reason = await flush();
      setLeaving(false);
      // Stays put on a failure. The words are still here, the reason is in the
      // footer with a Try again beside it, and the tab bar is still a way out
      // for somebody who would rather lose them than wait.
      if (reason !== null) return;
      const saved = rowRef.current;
      onClose(
        wroteRef.current && saved !== null
          ? { id: saved.id, heading: saved.heading, opened: note }
          : null,
      );
    })();
  };

  const isItem = row !== null && row.kind === 'item';
  const where = row === null ? null : whereLabel(row);
  const reading = view === 'read';
  // Whether an `<h2>` stands above the rendered body, which is the whole of what
  // decides the level `#` becomes - see `NoteMarkdown`'s `base`. An item note
  // always has one (the item's title); a note of its own has one in this view
  // exactly when it has been given a title.
  const titled = isItem || draft.title.trim() !== '';

  return (
    <>
      <div className="topbar">
        <button className="iconbtn" aria-label="Back to notes" disabled={leaving} onClick={leave}>
          <Icon name="chevL" />
        </button>
        {/* The SCREEN, not the note. The note names itself right underneath -
            in its own title field, or as the item's title it cannot be typed
            over - and a title bar repeating the line below it is one heading
            doing no work. The prototype's own title bar says this word. */}
        <h1>Note</h1>
        {row === null ? (
          // A note that does not exist yet cannot be deleted, and a button that
          // does nothing is worse than none. The heading stays centred.
          <span className="iconbtn" aria-hidden="true" />
        ) : (
          <button
            className="iconbtn"
            // Named for what it acts on, and for WHICH of the two things it
            // does: on an item it takes the note off and leaves the item alone,
            // which a bare "Delete" would not promise.
            aria-label={isItem ? `Remove the note on ${row.heading}` : `Delete ${row.heading}`}
            disabled={leaving}
            onClick={() => {
              if (row.kind === 'item') {
                void onDelete(row, () => clearRoutineNote(row)).then((removed) => {
                  if (!removed) void flush();
                });
                return;
              }

              discardedRef.current = true;
              if (timer.current !== null) clearTimeout(timer.current);
              abortRef.current?.abort();
              void onDelete(row).then((removed) => {
                if (removed) return;
                discardedRef.current = false;
                void flush();
              });
            }}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>

      <div
        className="scroll note-scroll"
        ref={visualPages.scrollRef}
        onScroll={visualPages.onScroll}
      >
        <div className="wrap">
          <div className="note-stage">
            {/* ONE CONTINUOUS DOCUMENT. The sheet edges and dividers are
                presentation-only children behind it: no text is split, copied,
                hidden or independently mounted, and the single textarea keeps
                its focus, selection and undo history across every boundary. */}
            <section
              className="note-paper"
              data-view={view}
              aria-label="Note document"
              ref={visualPages.paperRef}
              onClick={(event) => {
                if (view !== 'write' || event.target !== event.currentTarget) return;
                const field = bodyRef.current;
                if (field === null) return;
                field.focus();
                const end = field.value.length;
                field.setSelectionRange(end, end);
              }}
              style={
                visualPages.pageLength === 0
                  ? undefined
                  : ({
                      minHeight: `${visualPages.pageLength * visualPages.total}px`,
                    } as CSSProperties)
              }
            >
              <div className="note-page-guides" aria-hidden="true">
                {visualPageDividers(visualPages.total, visualPages.pageLength).map(
                  (offset, index) => (
                    <span
                      key={offset}
                      className="note-page-divider"
                      data-page={index + 2}
                      style={{ top: `${offset}px` }}
                    />
                  ),
                )}
              </div>
              <div className="note-paper-content" ref={visualPages.contentRef}>
                <div className="editor">
                  {isItem && row !== null ? (
                    // Drawn, not typed in: this is the ITEM's title. The hue is its
                    // schedule's, set as `--c` the way every other surface does it.
                    <div
                      className="notehead"
                      style={
                        row.schedule === null
                          ? undefined
                          : ({ '--c': hueIn(row.schedule.colorHex, mode) } as CSSProperties)
                      }
                    >
                      <h2>{row.title}</h2>
                      {where !== null && (
                        <p className="notewhere">
                          {row.schedule !== null && <i className="huedot" aria-hidden="true" />}
                          {where}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* The title is a FIELD, not markdown - it is never parsed.
                    Drawn in Read as the heading it already is, so that view has
                    nothing editable in it and so the rendered body has the
                    `<h2>` above it that `titled` promises `NoteMarkdown`. */}
                      {reading && draft.title.trim() !== '' && (
                        <h2 className="ed-titleread">{draft.title}</h2>
                      )}

                      {/* NEITHER FIELD IS BOUNDED HERE, and both were considered. A
                    `maxLength` swallows the tail of a paste without saying so,
                    which is data loss wearing a helpful face - on the one screen
                    whose bar is that it never loses what somebody typed. And the
                    number itself may not live in this client: a copy of a
                    server's limit tells somebody the wrong one the day it
                    changes, which is the whole reason the day payload carries
                    `cap` rather than restating it. So the words stay, the server
                    refuses what it will not take, and the footer draws the
                    sentence it sent. */}
                      <textarea
                        className="ed-title"
                        rows={1}
                        aria-label="Note title"
                        placeholder="Title"
                        hidden={reading}
                        ref={(field) => {
                          titleRef.current = field;
                          grow(field);
                        }}
                        value={draft.title}
                        disabled={leaving}
                        onFocus={(event) => {
                          writeFocusRef.current = event.currentTarget;
                        }}
                        onChange={(event) => {
                          grow(event.currentTarget);
                          change({ ...draftRef.current, title: event.target.value });
                        }}
                      />
                    </>
                  )}

                  {reading && <NoteMarkdown body={draft.body} base={titled ? 3 : 2} />}

                  {/* HIDDEN IN READ, NOT UNMOUNTED, and that is the whole reason this
                is `hidden` rather than a branch. THE UNDO STACK BELONGS TO THE
                ELEMENT: a textarea taken off screen and put back is a NEW field
                with an empty history, so Cmd+E to check a note and Cmd+E back
                would silently cost every undo made before it. Measured in
                Chrome, which is also where it was measured that this shape
                keeps them. The caret comes back with it, for free.

                `hidden` and not a class: it takes the field out of the
                accessibility tree and out of the tab order as well as out of
                the layout, which `display:none` in a stylesheet would do only by
                accident of the same declaration. */}
                  <textarea
                    className="ed-body"
                    aria-label="Note text"
                    placeholder={isItem ? PLACEHOLDER.item : PLACEHOLDER.note}
                    hidden={reading}
                    // The one field on a brand-new note worth landing in.
                    autoFocus={row === null}
                    ref={(field) => {
                      bodyRef.current = field;
                      grow(field);
                    }}
                    value={draft.body}
                    disabled={leaving}
                    onFocus={(event) => {
                      writeFocusRef.current = event.currentTarget;
                    }}
                    // R2. Every branch either returns an `Edit` or null, and null is
                    // the textarea's own behaviour reaching the browser untouched -
                    // which is why Tab outside a list still moves focus.
                    onKeyDown={(event) => {
                      const edit = editFor(event);
                      if (edit === null) return;
                      event.preventDefault();
                      applyEdit(event.currentTarget, edit);
                    }}
                    // A URL PASTED OVER A SELECTION BECOMES A LINK, and nothing else
                    // about a paste changes: `isUrl` takes one token with a scheme
                    // this note's links may carry, so a pasted paragraph is a pasted
                    // paragraph. Anything else would be a surprise with no undo
                    // anybody would think to reach for.
                    onPaste={(event) => {
                      const field = event.currentTarget;
                      const from = field.selectionStart ?? 0;
                      const to = field.selectionEnd ?? from;
                      if (from === to) return;
                      const pasted = event.clipboardData.getData('text/plain');
                      if (!isUrl(pasted)) return;
                      // A selection with no words in it has nothing to name, so the
                      // paste stays an ordinary paste.
                      const edit = smartEdit(field.value, from, to, () =>
                        linkSelection(field.value, from, to, pasted.trim()),
                      );
                      if (edit === null) return;
                      event.preventDefault();
                      applyEdit(field, edit);
                    }}
                    onChange={(event) => {
                      grow(event.currentTarget);
                      change({ ...draftRef.current, body: event.target.value });
                    }}
                  />
                </div>
              </div>
            </section>

            {/* UNDER THE DOCUMENT, and only once there is a note to share. A draft
                nobody has typed into has no row on the server yet, and an ITEM's
                note lives in `routine.note`, which has no share to give - so
                neither draws the block rather than drawing one that would be
                refused. See `ShareNote` for why it is here rather than in the
                title bar. */}
            {row !== null && row.kind === 'note' && (
              <ShareNote
                note={row}
                shared={shareStatus}
                onChanged={onShareChanged}
                onSignedOut={onSignedOut}
                say={say}
                saveBeforeSharing={saveBeforeSharing}
              />
            )}
          </div>
        </div>
      </div>

      <div className="ed-foot">
        {/* Held to the paper's width, so the count sits under the document
            rather than out at the edge of a 1440px window. The bar behind it
            still spans the pane, because a floating strip of surface reads as
            broken. */}
        <div className="ed-footin">
          <span className="sp tnum">{wordsLabel(draft.body)}</span>
          <VisualPageNavigator
            current={visualPages.current}
            total={visualPages.total}
            onJump={visualPages.jumpTo}
          />
          {/* THE ONE RESTING CONTROL THIS SLICE ADDS, and it is a view switch
              rather than a formatting toolbar. It sits in the footer because
              that is this screen's status bar - where Obsidian puts the same
              control - and because the top bar is already Back, the screen's
              name and the bin, which is as many things as a 390px phone holds
              without the heading drifting off centre.

              `aria-pressed` on two buttons rather than radios, which is this
              client's settled way of drawing a chooser (`edit/Sheet.tsx`,
              Settings' own segments), and the two words say what both states
              are - an icon alone would say neither. */}
          <div className="seg segmini" role="group" aria-label="View">
            {VIEWS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={view === option.value}
                disabled={leaving}
                onClick={() => setView(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {status === 'failed' ? (
            <>
              <span className="saved bad" role="alert">
                Not saved. {failure}
              </span>
              {/* Only where trying again could end differently. A refused
                  request cannot: the sentence beside this says which field and
                  what the limit is, and the fix is in the field. */}
              {retryable && (
                <button className="btn" onClick={() => void flush()}>
                  Try again
                </button>
              )}
            </>
          ) : (
            <span className="saved" role="status">
              {status === 'saving' && 'Saving…'}
              {status === 'saved' && (
                <>
                  <Icon name="check" />
                  Saved
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
